import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as issueApi from '../services/issueService';
import type { CreateIssueData, UpdateIssueData } from '../services/issueService';

export const issueKeys = {
  all: (projectId: string) => ['issues', projectId] as const,
  detail: (projectId: string, id: string) => ['issues', projectId, id] as const,
  activity: (projectId: string, id: string) => ['issues', projectId, id, 'activity'] as const,
  availableTransitions: (projectId: string, id: string) => ['issues', projectId, id, 'transitions'] as const,
  filters: () => ['filters'] as const,
};

// --- Issue list ---
export function useIssues(
  projectId: string,
  params?: { filter?: string; page?: number; size?: number; sort?: string },
) {
  return useQuery({
    queryKey: [...issueKeys.all(projectId), params],
    queryFn: () => issueApi.fetchIssues(projectId, params),
    enabled: !!projectId,
  });
}

// --- Issue detail ---
export function useIssue(projectId: string, issueId: string) {
  return useQuery({
    queryKey: issueKeys.detail(projectId, issueId),
    queryFn: () => issueApi.fetchIssue(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}

// --- Create issue ---
export function useCreateIssue(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateIssueData) => issueApi.createIssue(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueKeys.all(projectId) });
    },
  });
}

// --- Update issue ---
export function useUpdateIssue(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { issueId: string; data: UpdateIssueData }) =>
      issueApi.updateIssue(projectId, params.issueId, params.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: issueKeys.all(projectId) });
      queryClient.invalidateQueries({
        queryKey: issueKeys.detail(projectId, vars.issueId),
      });
      // CHG-011: 수정 후 활동 로그도 갱신
      queryClient.invalidateQueries({
        queryKey: issueKeys.activity(projectId, vars.issueId),
      });
    },
  });
}

// --- Delete issue ---
export function useDeleteIssue(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) => issueApi.deleteIssue(projectId, issueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueKeys.all(projectId) });
    },
  });
}

// --- Transition ---
export function useTransitionIssue(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { issueId: string; statusId: string; resolution?: string }) =>
      issueApi.transitionIssue(projectId, params.issueId, params.statusId, params.resolution),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: issueKeys.all(projectId) });
      queryClient.invalidateQueries({
        queryKey: issueKeys.detail(projectId, vars.issueId),
      });
      // 스프린트 보드에서 상태 전이 시 스프린트 데이터도 갱신
      queryClient.invalidateQueries({ queryKey: ['sprints', projectId] });
    },
  });
}

// --- Available Transitions (CHG-043: REQ-V43-016) ---
export function useAvailableTransitions(projectId: string, issueId: string) {
  return useQuery({
    queryKey: issueKeys.availableTransitions(projectId, issueId),
    queryFn: () => issueApi.fetchAvailableTransitions(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}

// --- Add comment ---
export function useAddComment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { issueId: string; content: string; parentCommentId?: string | null }) =>
      issueApi.addComment(projectId, params.issueId, params.content, params.parentCommentId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: issueKeys.detail(projectId, vars.issueId),
      });
      queryClient.invalidateQueries({
        queryKey: issueKeys.activity(projectId, vars.issueId),
      });
    },
  });
}

// --- Update comment ---
export function useUpdateComment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { issueId: string; commentId: string; content: string }) =>
      issueApi.updateComment(projectId, params.issueId, params.commentId, params.content),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: issueKeys.detail(projectId, vars.issueId) });
    },
  });
}

// --- Delete comment ---
export function useDeleteComment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { issueId: string; commentId: string }) =>
      issueApi.deleteComment(projectId, params.issueId, params.commentId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: issueKeys.detail(projectId, vars.issueId) });
      queryClient.invalidateQueries({ queryKey: issueKeys.activity(projectId, vars.issueId) });
    },
  });
}

// --- Toggle reaction ---
export function useToggleCommentReaction(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { issueId: string; commentId: string; emoji: string; remove: boolean }) =>
      params.remove
        ? issueApi.removeCommentReaction(projectId, params.issueId, params.commentId, params.emoji)
        : issueApi.addCommentReaction(projectId, params.issueId, params.commentId, params.emoji),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: issueKeys.detail(projectId, vars.issueId) });
    },
  });
}

// --- Upload attachments ---
export function useUploadAttachments(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { issueId: string; files: File[]; onProgress?: (p: number) => void }) =>
      issueApi.uploadAttachments(projectId, params.issueId, params.files, params.onProgress),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: issueKeys.detail(projectId, vars.issueId),
      });
    },
  });
}

// --- Delete attachment ---
export function useDeleteAttachment(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { issueId: string; attachmentId: string }) =>
      issueApi.deleteAttachment(projectId, params.issueId, params.attachmentId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: issueKeys.detail(projectId, vars.issueId),
      });
    },
  });
}

// --- Activity logs ---
export function useActivityLogs(projectId: string, issueId: string) {
  return useQuery({
    queryKey: issueKeys.activity(projectId, issueId),
    queryFn: () => issueApi.fetchActivityLogs(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}

// --- Filters ---
export function useFilters(params?: { scope?: string; projectId?: string }) {
  return useQuery({
    queryKey: [...issueKeys.filters(), params],
    queryFn: () => issueApi.fetchFilters(params),
  });
}

export function useCreateFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; query?: string; projectId?: string; scope?: string; filterConfig?: unknown }) =>
      issueApi.createFilter(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueKeys.filters() });
      queryClient.invalidateQueries({ queryKey: ['filters'] });
    },
  });
}

export function useDeleteFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filterId: string) => issueApi.deleteFilter(filterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueKeys.filters() });
      queryClient.invalidateQueries({ queryKey: ['filters'] });
    },
  });
}

export function useToggleFilterFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filterId: string) => issueApi.toggleFilterFavorite(filterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueKeys.filters() });
      queryClient.invalidateQueries({ queryKey: ['filters'] });
    },
  });
}

export function useFilter(filterId?: string) {
  return useQuery({
    queryKey: ['filters', filterId],
    queryFn: () => issueApi.fetchFilter(filterId!),
    enabled: !!filterId,
  });
}

export function useUpdateFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { filterId: string; data: { name?: string; query?: string; scope?: string; filterConfig?: unknown } }) =>
      issueApi.updateFilter(params.filterId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueKeys.filters() });
      queryClient.invalidateQueries({ queryKey: ['filters'] });
    },
  });
}

// ── CHG-085: 글로벌 이슈 + 필터 옵션 ──

export function useGlobalIssues(params?: { filter?: string; page?: number; size?: number; sort?: string }) {
  return useQuery({
    queryKey: ['issues', 'global', params],
    queryFn: () => issueApi.fetchGlobalIssues(params),
    enabled: params !== undefined,
  });
}

export function useFilterOptions() {
  return useQuery({
    queryKey: ['issues', 'filter-options'],
    queryFn: () => issueApi.fetchFilterOptions(),
    staleTime: 60_000,
  });
}
